import type * as type from "@vTypes";
import { server_url } from "@script/config";
import type { NurseResponse } from "@vTypes/nurse-type";
// const server_url = "http://localhost:8000";
// const server_url = `${import.meta.env.VITE_API_BASE_URL}`;

export async function saveNurseSequence(nurse_id: string, newSequence: number) {
  try {
    const response = await fetch(`${server_url}/nurses/sequence/save`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        nurse_id,
        new_sequence: newSequence,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData?.detail || "저장에 실패했습니다.");
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function saveChangeFullNurseSchema(
  data: NurseResponse[],
  groupId?: string | null,
  officeId?: string | null
) {
  const url = groupId
    ? `${server_url}/nurses/bulk-update?group_id=${encodeURIComponent(
        String(groupId)
      )}`
    : `${server_url}/nurses/bulk-update`;

  type NurseRow = type.FullNurseSchema & {
    office_id?: string | null;
    group_id?: string | null;
    EmpAuthGbn?: string | null;
    is_master_admin?: boolean;
  };

  const payload: Array<NurseRow> = data.map((row: NurseRow) => {
    const normalizedGroupId = groupId ?? row.group_id ?? null;
    return {
      ...row,
      office_id: row.office_id ?? officeId ?? null,
      // Ensure EmpAuthGbn exists (nullable allowed by backend)
      EmpAuthGbn: (row as any).EmpAuthGbn ?? null,
      is_master_admin: (row as any).is_master_admin ?? false,
      group_id: normalizedGroupId,
    } as NurseRow;
  });

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json();
    if (err.detail[0].type === "too_long") {
      console.error(err.detail[0].msg);
      throw new Error("전담 근무는 최대 3개 반영 가능합니다.");
    }
    throw new Error("저장에 실패했습니다.");
  }

  return (await response.json()) as unknown;
}

export async function saveFirstWantedDeadline(
  year: number,
  month: number,
  exp_date: Date | null
) {
  try {
    const base = `${server_url}/wanted/request`;
    const gid =
      typeof window !== "undefined"
        ? window.localStorage.getItem("rc_create_selected_group") ||
          window.localStorage.getItem("selectedGroupId") ||
          undefined
        : undefined;
    const url = gid ? `${base}?group_id=${encodeURIComponent(gid)}` : base;

    // Date 객체를 YYYY-MM-DD 형식의 문자열로 변환 (타임존 문제 방지)
    const exp_date_str = exp_date
      ? `${exp_date.getFullYear()}-${String(exp_date.getMonth() + 1).padStart(2, "0")}-${String(exp_date.getDate()).padStart(2, "0")}`
      : null;

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ year, month, exp_date: exp_date_str }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err?.detail || "마감일 저장에 실패했습니다.");
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function requestWantedDeadline(
  year: number,
  month: number,
  exp_date: Date | null
) {
  try {
    const base = `${server_url}/wanted/request`;
    const gid =
      typeof window !== "undefined"
        ? window.localStorage.getItem("rc_create_selected_group") ||
          window.localStorage.getItem("selectedGroupId") ||
          undefined
        : undefined;
    const url = gid ? `${base}?group_id=${encodeURIComponent(gid)}` : base;

    // Date 객체를 YYYY-MM-DD 형식의 문자열로 변환 (타임존 문제 방지)
    const exp_date_str = exp_date
      ? `${exp_date.getFullYear()}-${String(exp_date.getMonth() + 1).padStart(2, "0")}-${String(exp_date.getDate()).padStart(2, "0")}`
      : null;

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ year, month, exp_date: exp_date_str }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err?.detail || "마감일 저장에 실패했습니다.");
    }
  } catch (error) {
    console.error("requestWantedDeadline error", error);
    throw error;
  }
}

export async function saveChangeWantedDeadline(
  year: number,
  month: number,
  exp_date: Date | null
) {
  try {
    const base = `${server_url}/wanted/deadline`;
    const gid =
      typeof window !== "undefined"
        ? window.localStorage.getItem("rc_create_selected_group") ||
          window.localStorage.getItem("selectedGroupId") ||
          undefined
        : undefined;
    const url = gid ? `${base}?group_id=${encodeURIComponent(gid)}` : base;

    // Date 객체를 YYYY-MM-DD 형식의 문자열로 변환 (타임존 문제 방지)
    const exp_date_str = exp_date
      ? `${exp_date.getFullYear()}-${String(exp_date.getMonth() + 1).padStart(2, "0")}-${String(exp_date.getDate()).padStart(2, "0")}`
      : null;

    const response = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ year, month, exp_date: exp_date_str }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err?.detail || "마감일 저장에 실패했습니다.");
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function resolveGroupId(pref?: string | null): string | undefined {
  if (typeof window === "undefined") return pref ?? undefined;
  const fromStorage =
    window.localStorage.getItem("rc_create_selected_group") ||
    window.localStorage.getItem("please_fix_key_selectedGroupId");
  return pref ?? ((fromStorage || undefined) as string | undefined);
}

export async function saveScheduleVersionName(
  scheduleId: string,
  newName: string,
  groupId?: string
) {
  try {
    const base = `${server_url}/roster/${scheduleId}/name`;
    const inferred = resolveGroupId(groupId);
    const url = inferred
      ? `${base}?group_id=${encodeURIComponent(inferred)}`
      : base;

    const response = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name: newName }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err?.detail || "근무표 이름 변경에 실패했습니다.");
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function roster_delete(schedule_id: string, groupId?: string) {
  try {
    const base = `${server_url}/roster/${schedule_id}`;
    const inferred = resolveGroupId(groupId);
    const url = inferred
      ? `${base}?group_id=${encodeURIComponent(inferred)}`
      : base;

    const response = await fetch(url, {
      method: "DELETE",
      credentials: "include",
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err?.detail || "삭제에 실패했습니다.");
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function generateRoster_fixed(
  year: number,
  month: number,
  fixedData: type.fixedData,
  configId: string | null,
  grade_strategy: string = "COMBINED"
) {
  try {
    const response = await fetch(`${server_url}/roster_create/hold_generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        year,
        month,
        fixed_cells: fixedData,
        config_id: configId,
        grade_strategy,
      }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err?.detail || "근무표 생성에 실패했습니다.");
    }
    return (await response.json()) as unknown;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
